import {useLocation} from 'react-router'
import {useTranslation} from 'react-i18next'

import {useAppSelector} from 'app/store'
import classNames from 'classnames'
import {usePoliciesQuery} from 'features/Policies/usePolicyQuery'
import {useFeatureFlag, usePolicy} from 'hooks'
import {Policy, PolicyRoutesEnum} from 'interfaces'
import {NavLink} from '../LeftNavLink'
import {CanIAccess, PERMISSIONS} from 'rbac'
import {RenderIf, TooltipBody, TooltipWrapper} from 'components'
import {useMobileViewport} from 'hooks/useMobileViewport'
import {MobileBackButton} from 'components/Mobile/MobileBackButton'
import {goToAllPolicies} from 'features/Policies/hooks/usePolicyMobileSwipe'

export function PoliciesLeftNavigation() {
  const {isSSLInspectionDisabled} = useFeatureFlag()
  const {selectedPolicyName} = useAppSelector(rdxSt => rdxSt.policies)
  const location = useLocation<{policyName: string}>()
  const {pathname} = location
  const {policies} = usePoliciesQuery()
  // A tenant with one policy is sent straight back to it from All Policies,
  // so the phone back control would go nowhere.
  const hasAllPoliciesList = policies?.length !== 1

  const {isBasePolicyToBeColored} = usePolicy()
  const isMobile = useMobileViewport()
  const {t} = useTranslation(['policies'])

  const sectionLinks = (
    <>
      <NavLink
        pathname={PolicyRoutesEnum.POLICIES_CONTENT}
        first
        current={pathname === PolicyRoutesEnum.POLICIES_CONTENT}
        linkName="Content"
        currentPolicyName={selectedPolicyName}
      />
      <CanIAccess reqPermissions={[PERMISSIONS.VIEW_POLICY_USERS.READ]}>
        <NavLink
          pathname={PolicyRoutesEnum.USERS}
          first
          current={pathname === PolicyRoutesEnum.USERS}
          linkName="Users"
          currentPolicyName={selectedPolicyName}
          classNames={`${
            selectedPolicyName === Policy.BasePolicy ? 'hidden' : 'block'
          }`}
        />
      </CanIAccess>

      <CanIAccess reqPermissions={[PERMISSIONS.VIEW_POLICY_CAC.READ]}>
        <NavLink
          pathname={PolicyRoutesEnum.CLOUD_APP}
          first
          current={pathname === PolicyRoutesEnum.CLOUD_APP}
          linkName="Cloud App"
          currentPolicyName={selectedPolicyName}
        />
      </CanIAccess>

      <CanIAccess reqPermissions={[PERMISSIONS.DLP_POLICY_CONFIGURATION.READ]}>
        <div
          data-cy="policies-dlp-nav-link-wrapper"
          data-testid="policies-dlp-nav-link-wrapper"
          // Show tooltip only when SSL Inspection is disabled
          {...(isSSLInspectionDisabled && {
            'data-tooltip-id': 'policies-left-navigation-tooltip',
          })}
        >
          <NavLink
            pathname={PolicyRoutesEnum.DLP}
            current={pathname === PolicyRoutesEnum.DLP}
            linkName="DLP"
            currentPolicyName={selectedPolicyName}
            disabled={isSSLInspectionDisabled}
          />
        </div>
      </CanIAccess>
      <CanIAccess reqPermissions={[PERMISSIONS.POLICY_BYPASS.READ]}>
        <NavLink
          pathname={PolicyRoutesEnum.BYPASS}
          first
          current={pathname === PolicyRoutesEnum.BYPASS}
          linkName="Bypass"
          currentPolicyName={selectedPolicyName}
        />
      </CanIAccess>
    </>
  )

  return (
    <div
      className={classNames(
        'flex flex-col items-start pt-3 px-5 h-full',
        'mobile:items-stretch mobile:px-0 mobile:pt-0',
        {
          'bg-dope-teal-100': isBasePolicyToBeColored,
        },
      )}
    >
      {isMobile ? (
        <>
          <RenderIf isTrue={hasAllPoliciesList}>
            <div className="flex h-12 items-center pl-1.5 pr-2.5">
              <MobileBackButton
                label={t('MOBILE_ALL_POLICIES')}
                ariaLabel={t('MOBILE_BACK_TO', {
                  target: t('MOBILE_ALL_POLICIES'),
                })}
                onClick={goToAllPolicies}
                dataCy="policies-mobile-back"
              />
            </div>
          </RenderIf>
          <nav
            className="policy-mobile-tabs flex gap-4 overflow-x-auto border-b-0.5 border-gray-1500 px-4"
            data-mobile-swipe-ignore
            data-cy="policies-mobile-tabs"
          >
            {sectionLinks}
          </nav>
        </>
      ) : (
        sectionLinks
      )}

      <TooltipWrapper
        id="policies-left-navigation-tooltip"
        noArrow
        noPadding
        render={() => (
          <TooltipBody>
            {'This feature requires SSL Inspection to be turned ON.'}
          </TooltipBody>
        )}
        className="z-20"
      />
    </div>
  )
}
